// Coalesce the world page's asynchronous static loads. Camera changes and
// other regions finishing can ask for the same resource again while it is in
// flight; each key gets one completion handler and changes publish together.

export interface LoadCoordinator {
  readonly run: <T>(
    key: string,
    request: () => Promise<T> | null,
    publish: (value: Awaited<T>) => void,
    failed: (error: unknown) => void,
  ) => void;
  readonly changed: () => void;
}

export function createLoadCoordinator(onChange: () => void): LoadCoordinator {
  const pending = new Set<string>();
  const retrying = new Set<string>();
  let changeQueued = false;

  function changed(): void {
    if (changeQueued) return;
    changeQueued = true;
    queueMicrotask(() => {
      changeQueued = false;
      onChange();
    });
  }

  function run<T>(
    key: string,
    request: () => Promise<T> | null,
    publish: (value: Awaited<T>) => void,
    failed: (error: unknown) => void,
  ): void {
    if (pending.has(key) || retrying.has(key)) return;
    let task: Promise<T> | null;
    try {
      task = request();
    } catch (error) {
      retry(error);
      return;
    }
    function retry(error: unknown): void {
      retrying.add(key);
      setTimeout(() => {
        retrying.delete(key);
        changed();
      }, 3000);
      failed(error);
    }
    if (!task) return;
    const started = task;
    pending.add(key);
    async function settle(): Promise<void> {
      try {
        const value = await started;
        publish(value);
        changed();
      } catch (error) {
        retry(error);
      } finally {
        pending.delete(key);
      }
    }
    void settle();
  }

  return { run, changed };
}
